import { Controller } from '@hotwired/stimulus';
import { DirectUpload } from '@rails/activestorage';
import { track } from '../lib/analytics';
// wavesurfer (~100KB) is loaded on demand in setupTrimmer — only the trimmer
// pages pay for it, not all 300+ pages sharing the bundle.

export default class extends Controller {
  static values = {
    genericErrorMessage: String,
    unsupportedFormatMessage: String,
    directUploadUrl: String,
  };

  static targets = [
    'uploadError',
    'uploadErrorMessage',
    'progressBarWrapper',
    'progressBar',
    'input',
    'uploadFormWrapper',
    'dropOverlay',
    'submitButton',
    'uploadFormContent',
    'configurationStep',
    'progressPercentage',
    'filename',
    'size',
    'fileCard',
    'fileTile',
    'finalStep',
    'trimmer',
    'trimmerStart',
    'trimmerEnd',
    'video',
    'playButton',
    'pauseButton',
    'backwardButton',
    'forwardButton',
    'trimmerStartInput',
    'trimmerEndInput',
    'trimmingUpsell',
  ];

  static states = {
    INITIAL: 'initial',
    UPLOADING: 'uploading',
    CONFIGURING: 'configuring',
    FINAL: 'final',
  };

  connect() {
    if (this.hasInputTarget) {
      this.inputTarget.value = null;
      this.element.addEventListener('dragenter', this.onDragEnter.bind(this));
      this.element.addEventListener('dragleave', this.onDragExit.bind(this));
      this.element.addEventListener('dragover', this.onDragOver.bind(this));
      this.element.addEventListener('drop', this.onDrop.bind(this));
      this.uploadFormContentTarget.addEventListener('click', this.onContainerClick.bind(this));
    }

    this.currentState = this.constructor.states.INITIAL;
    this.activeXHR = null;
    this.blobXHR = null;
    this.hide('configurationStep');
    this.hide('finalStep');
    this.wavesurfer = null;
    this.regions = null;
  }

  disconnect() {
    this.destroyTrimmer();
  }

  isFileUploadEnabled() {
    return (
      this.currentState === this.constructor.states.INITIAL &&
      !this.uploadFormWrapperTarget.classList.contains('hidden')
    );
  }

  onDrop(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isFileUploadEnabled()) {
      return;
    }

    track('Media trimmer file dropped');
    const item = event.dataTransfer.items[0];

    if (item) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        this.uploadFile(file);
        this.showProgressBar();
        this.dropOverlayTarget.classList.remove('opacity-100');
        this.dropOverlayTarget.classList.add('opacity-0', 'pointer-events-none');
      }
    }
  }

  onDragEnter(event) {
    event.preventDefault();

    if (!this.isFileUploadEnabled()) {
      return;
    }

    if (event.dataTransfer.types.includes('Files')) {
      this.dropOverlayTarget.classList.add('opacity-100');
      this.dropOverlayTarget.classList.remove('opacity-0', 'pointer-events-none');
    }
  }

  onDragExit(event) {
    if (!this.isFileUploadEnabled()) {
      return;
    }

    if (event.currentTarget === this.element && !this.element.contains(event.relatedTarget)) {
      this.dropOverlayTarget.classList.remove('opacity-100');
      this.dropOverlayTarget.classList.add('opacity-0', 'pointer-events-none');
    }
  }

  onDragOver(event) {
    if (!this.isFileUploadEnabled()) {
      return;
    }
    event.preventDefault();
  }

  start() {
    Array.from(this.inputTarget.files).forEach((file) => this.uploadFile(file));
    this.showProgressBar();
  }

  createHiddenInputs(signedId, filename) {
    this.createHiddenInput(this.inputTarget.name, signedId);
    this.createHiddenInput('filename', filename);
  }

  uploadFile(file) {
    const url = this.directUploadUrlValue;
    const upload = new DirectUpload(file, url, this);
    const filename = file.name;

    this.filenameTarget.textContent = filename;
    this.setFileTile(filename);
    this.sizeTarget.textContent = this.prettyPrintSize(file.size);

    upload.create((error, blob) => {
      if (error) {
        this.handleUploadError(error);
      } else {
        track('Media trimmer upload finished');
        this.hideProgressBar();
        this.createHiddenInputs(blob.signed_id, filename);
        this.setupTrimmer(URL.createObjectURL(file), file.type.includes('audio'))
          .then(() => {
            this.showConfigurationStep();
          })
          .catch((error) => {
            console.error('Error setting up trimmer:', error);
          });
    this.markFileTileReady();
      }
      this.activeXHR = null;
      this.blobXHR = null;
    });

    track('Media trimmer upload started');
  }

  limitString(string, length) {
    if (string.length >= length) {
      const trimmedString = string.slice(0, length).trimEnd();
      return `${trimmedString}...`;
    } else {
      return string;
    }
  }

  resumePlayback() {
    if (this.hasPauseButtonTarget) {
      this.pauseButtonTarget.classList.remove('hidden');
    }
    if (this.hasPlayButtonTarget) {
      this.playButtonTarget.classList.add('hidden');
    }
    this.wavesurfer?.play();
  }

  pausePlayback() {
    if (this.hasPauseButtonTarget) {
      this.pauseButtonTarget.classList.add('hidden');
    }
    if (this.hasPlayButtonTarget) {
      this.playButtonTarget.classList.remove('hidden');
    }
    this.wavesurfer?.pause();
  }

  async setupTrimmer(url, isAudio = true) {
    if (this.hasTrimmerTarget) {
      const [{ default: WaveSurfer }, { default: RegionsPlugin }] = await Promise.all([
        import('wavesurfer.js'),
        import('wavesurfer.js/plugins/regions'),
      ]);

      this.destroyTrimmer();

      if (this.hasVideoTarget) {
        if (isAudio) {
          this.videoTarget.classList.add('hidden');
        } else {
          this.videoTarget.classList.remove('hidden');
        }
      }

      this.regions = RegionsPlugin.create();

      this.wavesurfer = WaveSurfer.create({
        url: url,
        container: this.trimmerTarget,
        waveColor: '#ffc53d',
        cursorColor: 'red',
        cursorWidth: 2,
        barWidth: 4,
        barGap: 4,
        barRadius: 2,
        normalize: true,
        plugins: [this.regions],
        height: 80,
        media: this.hasVideoTarget && !isAudio ? this.videoTarget : null,
      });

      this.wavesurfer.on('error', (error) => {
        const message = error?.message ?? error?.error?.message ?? String(error);

        // Firefox queues a MEDIA_ERR_SRC_NOT_SUPPORTED error when videoTarget.src
        // is briefly emptied during the destroy → recreate cycle. The blob loads
        // fine right after, so this doesn't reflect a real codec problem.
        if (message.includes('MediaLoadInvalidURI') || message.includes('Invalid URI')) {
          return;
        }

        this.handleUnsupportedFormat();
      });

      let activeRegion;
      this.wavesurfer.on('decode', (duration) => {
        activeRegion = this.regions.addRegion({
          start: 0,
          end: duration,
          color: 'transparent',
          drag: true,
          content: '.',
          resize: true,
        });
        this.syncTrimmerInputs(activeRegion);
        this.wavesurfer?.setTime(activeRegion?.start || 0);
        if (this.hasTrimmerTarget) {
          this.trimmerTarget.parentElement?.classList?.remove('animate-pulse');
        }
      });

      this.wavesurfer.on('finish', () => {
        this.wavesurfer?.setTime(activeRegion?.start || 0);
        this.resumePlayback();
      });

      this.regions.on('region-out', (region) => {
        this.wavesurfer?.setTime(region.start);
      });

      this.regions.on('region-update', (region) => {
        if (
          region.start > this.wavesurfer?.getCurrentTime() ||
          region.end < this.wavesurfer?.getCurrentTime()
        ) {
          this.wavesurfer?.setTime(region.start);
        }
        this.syncTrimmerInputs(region);
      });

      if (this.hasPlayButtonTarget) {
        this.playButtonTarget.addEventListener('click', () => {
          this.resumePlayback();
        });
      }
      if (this.hasPauseButtonTarget) {
        this.pauseButtonTarget.addEventListener('click', () => {
          this.pausePlayback();
        });
      }
      if (this.hasBackwardButtonTarget) {
        this.backwardButtonTarget.addEventListener('click', () => {
          this.wavesurfer?.skip(-5);
        });
      }
      if (this.hasForwardButtonTarget) {
        this.forwardButtonTarget.addEventListener('click', () => {
          this.wavesurfer?.skip(5);
        });
      }
    }
  }

  syncTrimmerInputs(region) {
    if (this.hasTrimmerStartInputTarget) {
      this.trimmerStartInputTarget.value = region.start;
    }
    if (this.hasTrimmerEndInputTarget) {
      this.trimmerEndInputTarget.value = region.end;
    }

    if (this.hasTrimmerStartTarget) {
      this.trimmerStartTarget.textContent = formatTime(region.start);
    }
    if (this.hasTrimmerEndTarget) {
      this.trimmerEndTarget.textContent = formatTime(region.end);
    }
  }

  destroyTrimmer() {
    this.pausePlayback();
    this.wavesurfer?.destroy();
    this.regions?.destroy();
    this.wavesurfer = null;
    this.regions = null;
    if (this.hasTrimmerTarget) {
      this.trimmerTarget.innerHTML = '';
      this.trimmerTarget.parentElement?.classList?.add('animate-pulse');
    }
    if (this.hasVideoTarget) {
      this.videoTarget.src = '';
    }
  }

  createHiddenInput(name, value) {
    const hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('value', value);
    hiddenField.name = name;

    const form = this.element.querySelector('form');
    if (form) {
      form.appendChild(hiddenField);
    } else {
      this.element.appendChild(hiddenField);
    }
  }

  // Callback method that gets called automatically by the Rails Active Storage DirectUpload
  directUploadWillCreateBlobWithXHR(request) {
    this.blobXHR = request;
  }

  // Callback method that gets called automatically by the Rails Active Storage DirectUpload
  directUploadWillStoreFileWithXHR(request) {
    this.activeXHR = request;
    request.upload.addEventListener('progress', (event) => this.setUploadProgress(event));

    request.addEventListener('error', (event) => {
      this.handleUploadError(event);
    });
  }

  setUploadProgress(event) {
    const value = Math.min(1, Math.max(0, event.loaded / event.total));
    const percentage = Math.floor(value * 100);
    this.progressBarTarget.style.width = `${percentage}%`;
    this.progressPercentageTarget.textContent = `${percentage}%`;
  }

  showProgressBar() {
    this.currentState = this.constructor.states.UPLOADING;
    this.hide('uploadFormContent');
    this.show('progressBarWrapper');
    this.show('fileCard');
    this.hide('configurationStep');
    this.disableSubmitButton();
    this.hide('finalStep');
    this.markFileTilePending();
    this.scrollToTop();
  }

  hideProgressBar() {
    this.progressBarTarget.style.width = '0%';
    this.progressPercentageTarget.textContent = '0%';
    this.hide('progressBarWrapper');
    this.show('fileCard');
    this.enableSubmitButton();
  }

  showConfigurationStep() {
    this.currentState = this.constructor.states.CONFIGURING;
    this.hide('progressBarWrapper');
    this.hide('uploadFormContent');
    this.hide('finalStep');
    this.show('configurationStep');
    this.enableSubmitButton();
    this.scrollToTop();
  }

  hideConfigurationStep() {
    this.hide('configurationStep');
    this.disableSubmitButton();
    this.destroyTrimmer();
    this.showTrimmingUpsell();
    if (this.hasFilenameTarget) {
      this.filenameTarget.textContent = '[trimmed] ' + this.filenameTarget.textContent;
    }
    if (this.hasSizeTarget) {
      this.sizeTarget.textContent = '';
    }
  }

  showFinalStep() {
    this.currentState = this.constructor.states.FINAL;
    this.hide('configurationStep');
    this.destroyTrimmer();
    this.hide('trimmingUpsell');
    this.show('finalStep');
    this.enableSubmitButton();
  }

  disableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
    }
  }

  enableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false;
    }
  }

  prettyPrintSize(size) {
    const mb = size / 1000000.0;

    if (mb < 1) {
      return `${parseFloat(mb.toFixed(2))} MB`;
    } else {
      return `${Math.floor(mb)} MB`;
    }
  }

  resetFileInput() {
    if (this.hasInputTarget) {
      this.inputTarget.value = null;
      this.filenameTarget.textContent = '';
      this.sizeTarget.textContent = '';
      this.hideProgressBar();
    }
  }

  deleteFile(event) {
    this.hide('uploadError');
    const stage = document.getElementById('progress_button');
    if (stage) stage.innerHTML = '';
    this.currentState = this.constructor.states.INITIAL;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.blobXHR) {
      this.blobXHR.abort();
      this.blobXHR = null;
    }

    if (this.activeXHR) {
      this.activeXHR.abort();
      this.activeXHR = null;
    }

    this.resetFileInput();
    this.hide('configurationStep');
    this.destroyTrimmer();
    this.hide('trimmingUpsell');
    this.hide('finalStep');
    this.hide('fileCard');
    this.show('uploadFormContent');

    track('Media trimmer upload cancelled');
  }

  onContainerClick(event) {
    if (!this.isFileUploadEnabled()) {
      return;
    }

    // Don't trigger if clicking on the file input or its label.
    // Otherwise, the open file dialog will be triggered twice.
    if (event.target === this.inputTarget || event.target.id === 'label-file') {
      return;
    }

    this.inputTarget.click();
  }

  handleUploadError(error) {
    console.error('Upload error:', error);
    this.showInlineError(this.genericErrorMessageValue);
  }

  showInlineError(message) {
    this.resetToStart();
    if (this.hasUploadErrorMessageTarget) this.uploadErrorMessageTarget.textContent = message;
    this.show('uploadError');
  }

  // Reset the flow without hiding the error banner (deleteFile clears it).
  resetToStart() {
    const saved = this.hasUploadErrorTarget && !this.uploadErrorTarget.classList.contains('hidden');
    this.deleteFile();
    if (saved) this.show('uploadError');
  }

  handleUnsupportedFormat() {
    this.showInlineError(this.unsupportedFormatMessageValue || this.genericErrorMessageValue);
  }

  showTrimmingUpsell() {
    this.show('trimmingUpsell');
  }

  show(targetName) {
    const hasTarget = this[`has${this.capitalizeFirst(targetName)}Target`];

    if (!hasTarget) return;

    const target = this[`${targetName}Target`];
    const targets = this[`${targetName}Targets`];
    if (targets) {
      targets.forEach((target) => {
        target.classList.remove('hidden');
      });
    } else if (target) {
      target.classList.remove('hidden');
    }
  }

  hide(targetName) {
    const hasTarget = this[`has${this.capitalizeFirst(targetName)}Target`];

    if (!hasTarget) return;
    const target = this[`${targetName}Target`];
    const targets = this[`${targetName}Targets`];
    if (targets) {
      targets.forEach((target) => {
        target.classList.add('hidden');
      });
    } else if (target) {
      target.classList.add('hidden');
    }
  }

  scrollToTop() {
    const nav = document.querySelector('nav, header');
    const offset = nav ? nav.offsetHeight + 16 : 16;
    const top = this.element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  capitalizeFirst(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  setFileTile(filename) {
    const ext = (filename.includes('.') ? filename.split('.').pop() : 'FILE').slice(0, 5).toUpperCase();
    this.fileTileTarget.textContent = ext || 'FILE';
  }

  markFileTileReady() {
    this.fileTileTarget.classList.remove('animate-pulse');
  }

  markFileTilePending() {
    this.fileTileTarget.classList.add('animate-pulse');
  }
}

const formatTime = (time) => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);
  const milliseconds = Math.round((time % 1) * 100);
  const milliSecondsString =
    milliseconds === 0 ? '' : `.${milliseconds.toString().padStart(2, '0')}`;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}${milliSecondsString}`;
};
